import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import { PriceListItem, PriceListItemUpdate } from '@/app/types/pricing';

// Schema for the EDIT form
const formSchema = z.object({
  rate: z.string().optional().nullable().refine(val => val === null || val === '' || (!isNaN(parseFloat(val)) && parseFloat(val) >= 0), { message: "Rate must be a non-negative number" }),
  discount_percent: z.string().optional().nullable().refine(val => val === null || val === '' || (!isNaN(parseFloat(val)) && parseFloat(val) >= 0 && parseFloat(val) <= 100), { message: "Discount % must be between 0 and 100" }),
  discount_amount: z.string().optional().nullable().refine(val => val === null || val === '' || (!isNaN(parseFloat(val)) && parseFloat(val) >= 0), { message: "Discount amount must be non-negative" }),
  min_qty: z.string().refine(val => !isNaN(parseFloat(val)) && parseFloat(val) >= 0, { message: "Min Qty must be non-negative" }),
  max_qty: z.string().optional().nullable().refine(val => val === null || val === '' || (!isNaN(parseFloat(val)) && parseFloat(val) >= 0), { message: "Max Qty must be non-negative" }),
  is_tax_inclusive: z.boolean(),
}).refine(data => {
    const definedOptions = [data.rate, data.discount_percent, data.discount_amount].filter(val => val !== null && val !== undefined && val !== '').length;
    return definedOptions <= 1;
  }, {
    message: "Specify only one: Rate, Discount %, or Discount Amount.",
    path: ["rate"],
});

type PriceListItemFormValues = z.infer<typeof formSchema>;

interface PriceListItemFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: PriceListItemUpdate) => void;
  defaultValues: PriceListItem;
  isLoading?: boolean;
}

const PriceListItemForm: React.FC<PriceListItemFormProps> = ({
  open,
  onOpenChange,
  onSubmit,
  defaultValues,
  isLoading = false,
}) => {
  const form = useForm<PriceListItemFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
        rate: defaultValues.rate !== null && defaultValues.rate !== undefined ? String(defaultValues.rate) : null,
        discount_percent: defaultValues.discount_percent !== null && defaultValues.discount_percent !== undefined ? String(defaultValues.discount_percent) : null,
        discount_amount: defaultValues.discount_amount !== null && defaultValues.discount_amount !== undefined ? String(defaultValues.discount_amount) : null,
        min_qty: String(defaultValues.min_qty ?? 0),
        max_qty: defaultValues.max_qty !== null && defaultValues.max_qty !== undefined ? String(defaultValues.max_qty) : null,
        is_tax_inclusive: defaultValues.is_tax_inclusive ?? false,
    },
  });

  const handleSubmit: SubmitHandler<PriceListItemFormValues> = (values) => {
    const payload: PriceListItemUpdate = {
        rate: values.rate ? parseFloat(values.rate) : null,
        discount_percent: values.discount_percent ? parseFloat(values.discount_percent) : null,
        discount_amount: values.discount_amount ? parseFloat(values.discount_amount) : null,
        min_qty: parseFloat(values.min_qty),
        max_qty: values.max_qty ? parseFloat(values.max_qty) : null,
        is_tax_inclusive: values.is_tax_inclusive,
    };
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Pricing for: {defaultValues.item?.name ?? 'Item'}</DialogTitle>
          <DialogDescription>Update the pricing details for this item within the price list.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 py-2">
             <div className="grid grid-cols-3 gap-3">
                <FormField control={form.control} name="rate" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rate</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        type="number" 
                        step="0.01" 
                        placeholder="Price" 
                        value={field.value ?? ''} 
                        onChange={e => field.onChange(e.target.value === '' ? null : e.target.value)} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="discount_percent" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Disc %</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        type="number" 
                        step="0.01" 
                        placeholder="%" 
                        value={field.value ?? ''} 
                        onChange={e => field.onChange(e.target.value === '' ? null : e.target.value)} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="discount_amount" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Disc Amt</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        type="number" 
                        step="0.01" 
                        placeholder="Amount" 
                        value={field.value ?? ''} 
                        onChange={e => field.onChange(e.target.value === '' ? null : e.target.value)} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
             </div>
             <p className="text-xs text-muted-foreground px-1">Set only one pricing method (Rate, Discount %, or Discount Amount).</p>

             <div className="grid grid-cols-2 gap-4">
                <FormField control={form.control} name="min_qty" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Min Qty*</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        type="number" 
                        step="0.001" 
                        placeholder="0" 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="max_qty" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max Qty</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        type="number" 
                        step="0.001" 
                        placeholder="Optional" 
                        value={field.value ?? ''} 
                        onChange={e => field.onChange(e.target.value === '' ? null : e.target.value)} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
             </div>

            <FormField control={form.control} name="is_tax_inclusive" render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-3 space-y-0 pt-2">
                <FormControl>
                  <Checkbox 
                    checked={field.value} 
                    onCheckedChange={field.onChange} 
                  />
                </FormControl>
                <FormLabel className="font-normal">Price is Tax Inclusive</FormLabel>
              </FormItem>
            )} />

            <DialogFooter className='pt-4'>
              <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} disabled={isLoading}>Cancel</Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default PriceListItemForm; 